import React from "react";
import { useLang } from "@/context/LanguageContext";
import { Phone, MessageCircle, Star, ShieldCheck, Building2, Users } from "lucide-react";

/**
 * Shown on Property Detail page for Broker/Builder-owned properties.
 * Reveals contact + rating + business info (contact would otherwise be redacted).
 */
export default function PartnerContactCard({ property }) {
  const { lang } = useLang();
  if (!property?.owner_partner_role || !["broker", "builder"].includes(property.owner_partner_role)) return null;

  const role = property.owner_partner_role;
  const label = role === "builder" ? (lang === "hi" ? "बिल्डर" : "Builder") : (lang === "hi" ? "ब्रोकर" : "Broker");
  const RoleIcon = role === "builder" ? Building2 : Users;
  const roleTint = role === "builder" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-primary/5 border-primary/40 text-primary";
  const phone = property.owner_phone || property.contact_phone;
  const cleanPhone = phone?.replace(/[^0-9+]/g, "");
  const wa = cleanPhone ? `https://wa.me/${cleanPhone.replace(/^\+?/, "").replace(/^0/, "91")}` : null;

  const initials = (property.owner_business_name || property.owner_name || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <div className={`p-5 rounded-xl border-2 ${roleTint}`} data-testid="partner-contact-card">
      <div className="flex items-center gap-3 mb-3">
        {property.owner_photo_url ? (
          <img
            src={property.owner_photo_url}
            alt={property.owner_name || "Partner"}
            className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-white grid place-items-center text-xl font-bold shadow">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest opacity-80">
            <RoleIcon size={11} /> {label}
            {property.owner_kyc_status === "verified" && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700"><ShieldCheck size={10} /> KYC</span>
            )}
          </div>
          <div className="font-bold text-base leading-tight mt-0.5 truncate" data-testid="partner-name">
            {property.owner_business_name || property.owner_name || "Partner"}
          </div>
          {property.owner_rating_avg != null ? (
            <div className="flex items-center gap-1 text-[11px] mt-0.5">
              <Star size={11} className="fill-yellow-500 text-yellow-500" />
              <span className="font-semibold">{property.owner_rating_avg}</span>
              <span className="opacity-70">({property.owner_rating_count})</span>
            </div>
          ) : (
            <div className="text-[11px] opacity-70 mt-0.5">
              {lang === "hi" ? "अभी तक कोई रिव्यू नहीं" : "No reviews yet"}
            </div>
          )}
        </div>
      </div>

      {property.owner_bio && (
        <p className="text-xs opacity-90 mb-3 line-clamp-3">{property.owner_bio}</p>
      )}

      {phone && (
        <div className="grid grid-cols-2 gap-2">
          <a
            href={`tel:${cleanPhone}`}
            className="flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold bg-white/80 hover:bg-white transition"
            data-testid="partner-call-btn"
          >
            <Phone size={13} /> {lang === "hi" ? "कॉल करें" : "Call"}
          </a>
          {wa && (
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition"
              data-testid="partner-whatsapp-btn"
            >
              <MessageCircle size={13} /> WhatsApp
            </a>
          )}
        </div>
      )}
    </div>
  );
}
